import React, { useState, useRef } from 'react';
import { CreditCard, Shield, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { recurringPaymentsApi } from '../../../../services/recurringPaymentsApi';
import { userErrorMessage } from '../../../../lib/userErrors';
import { CardFields, useEpaycoCardTokenizer } from './CardFields';

interface CardRegistrationFormProps {
    onSuccess: () => void;
    onCancel: () => void;
    international?: boolean;
    phoneCountry?: string;
}

export function CardRegistrationForm({
    onSuccess,
    onCancel,
    international = false,
    phoneCountry = 'CO',
}: CardRegistrationFormProps) {
    const { ready, loadingKey, initError, tokenize, fields } = useEpaycoCardTokenizer();
    const formRef = useRef<HTMLFormElement>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formRef.current) return;

        setLoading(true);
        setError(null);

        let card;
        try {
            card = await tokenize(formRef.current);
        } catch (err: any) {
            setError(err.message);
            setLoading(false);
            return;
        }

        try {
            await recurringPaymentsApi.registerCard({ ...card, set_as_default: true });
            onSuccess();
        } catch (err: any) {
            setError(userErrorMessage(err, 'Error al registrar la tarjeta.'));
        } finally {
            setLoading(false);
        }
    };

    if (loadingKey) {
        return (
            <div className="p-8 flex flex-col items-center justify-center text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-4 text-[#1CB3B3]" />
                <p>Iniciando conexión segura con ePayco...</p>
            </div>
        );
    }

    const shownError = error || initError;

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm w-full">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#E8F7F7] rounded-full flex items-center justify-center border border-[#CDEEEE]">
                    <CreditCard className="w-5 h-5 text-[#1CB3B3]" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-slate-900">Añadir Tarjeta</h3>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Lock className="w-3 h-3 text-emerald-500" />
                        Los datos se encriptan punto a punto
                    </p>
                </div>
            </div>

            {shownError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>{shownError}</p>
                </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                <CardFields fields={fields} international={international} phoneCountry={phoneCountry} />

                <div className="pt-4 flex gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="flex-1 px-4 py-3 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading || !ready}
                        className="flex-1 px-4 py-3 rounded-xl font-bold text-white bg-[#1CB3B3] hover:bg-[#169a9a] transition flex justify-center items-center shadow-sm disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Guardar Tarjeta'}
                    </button>
                </div>

                <p className="text-center text-[10px] text-slate-500 flex justify-center gap-1 mt-2">
                    <Shield className="w-3 h-3 text-slate-400" />
                    Tus datos de pago se procesan de forma segura
                </p>
            </form>
        </div>
    );
}
